import * as z from "zod";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../providers/form";
import InputField from "widgets/shared/inputs/inputField";
import { useTranslation } from "react-i18next";
import GenericButton from "widgets/controls/buttons/generic";
import useWhiteLabel from "whitelabel/useWhiteLabel";
import { useEffect } from "react";
import SitesCombobox from "widgets/shared/inputs/usersCombobox";
// Schema
const DeviceGeneralSchema = z.object({
  meterNumber: z.string(),
  meterModel: z.string(),
  host: z.string(),
  unitNumber: z.string(),
  siteId: z.string().optional(),
});

export type DeviceGeneralFormType = z.infer<typeof DeviceGeneralSchema>;

// Props
type DeviceGeneralFormProps = {
  submit: (data: DeviceGeneralFormType) => void;
  onClose: () => void;
  defaultValues?: DeviceGeneralFormType;
  values?: DeviceGeneralFormType;
  loading?: boolean;
  disabled?: boolean;
};

const DeviceGeneralForm = ({
  submit,
  onClose,
  defaultValues,
  values,
  loading,
  disabled,
}: DeviceGeneralFormProps) => {
  const form = useForm<DeviceGeneralFormType>({
    resolver: zodResolver(DeviceGeneralSchema),
    defaultValues: {
      meterNumber: "",
      meterModel: "",
      host: "",
      unitNumber: "",
    },
    values,
  });

  const { t } = useTranslation();
  const { platform } = useWhiteLabel();
  const { errors, isDirty } = form.formState;

  const clearErrors = () => {
    form.clearErrors();
  };

  // Automatically submit the form if valid
  const onSubmit = async (formData: DeviceGeneralFormType) => {
    try {
      await submit(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  useEffect(() => {
    if (disabled && isDirty) {
      form.reset(values || defaultValues);
    }
  }, [disabled, isDirty, form, values, defaultValues]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className=" space-y-2">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
          <FormField
            control={form.control}
            name="meterNumber"
            rules={{ required: false }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.input.meter.number")}</FormLabel>
                <FormControl>
                  <InputField
                    placeholder={t("form.placeholder.meter.number")}
                    id="meterNumber-input"
                    type="text"
                    state={errors.meterNumber ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="meterModel"
            rules={{ required: false }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.input.meter.model")}</FormLabel>
                <FormControl>
                  <InputField
                    placeholder={t("form.placeholder.meter.model")}
                    id="meterModel-input"
                    type="text"
                    state={errors.meterModel ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
          <FormField
            control={form.control}
            name="host"
            rules={{ required: false }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.input.address")}</FormLabel>
                <FormControl>
                  <InputField
                    placeholder={t("form.placeholder.address")}
                    id="host-input"
                    type="text"
                    state={errors.host ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="unitNumber"
            rules={{ required: false }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{platform === "thermocloud" ? "Patriot Zone No" : t("form.input.unit.number")}</FormLabel>
                <FormControl>
                  <InputField
                    placeholder={t("form.placeholder.unit.number")}
                    id="unitNumber-input"
                    type="text"
                    state={errors.unitNumber ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-5">
          <FormField
            control={form.control}
            name="siteId"
            rules={{ required: false }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("form.input.site")}</FormLabel>
                <FormControl>
                  <SitesCombobox
                    placeholder={t("form.placeholder.select.site")}
                    id="siteId-combobox"
                    type="text"
                    state={errors.siteId ? "error" : undefined}
                    onFocus={clearErrors}
                    color="secondary"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex justify-end py-2">
          <div className="flex gap-2">
            <GenericButton
              onClick={onClose}
              text={t("button.action.cancel")}
              size="md"
              variant="ghost"
              color="secondary"
            />

            <GenericButton
              type="submit"
              text={t("button.action.save")}
              loading={loading || false}
              size="md"
            />
          </div>
        </div>
      </form>
    </Form>
  );
};

export default DeviceGeneralForm;
